import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, LinearProgress } from '@mui/material';
import deepmerge from 'deepmerge';
import _ from 'lodash';

import { getProductsAction, getProductsState } from '../../../features/product/get_products';
import ProductsList from './products_list';
import PaginationControls from '../../common/pagination_controls';
import { getDefaultProductPaginationQueryValues } from '../../../core/utils/api_utils';
import ProductFilters from './product_filters';
import useNotification from '../../../core/hooks/use_notification';
import { getAllFoodPartnersNotificationData, getConstantsNotificationData, getProductsNotificationData } from '../../../core/utils/notification_utils';
import { Button } from '../../common/button';
import { getAllFoodPartnersAction, getAllFoodPartnersState } from '../../../features/food_partner/get_all_food_partners';
import { getConstantsAction, getConstantsState } from '../../../features/product/get_constants';
import PageHeader from '../../common/page_header';
import { ROUTE_PRODUCT_FORMS_NEW } from "../../../core/constants";

const initialQueryParams = {
    ...getDefaultProductPaginationQueryValues,
    query: '',
    searchCode: 'all',
    sort: 'id,asc'
};

const ProductsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { showErrorMessage } = useNotification();
    const location = useLocation();

    const getConstants = useSelector(getConstantsState);
    const getAllFoodPartners = useSelector(getAllFoodPartnersState);
    const getProducts = useSelector(getProductsState);

    const [queryParams, setQueryParams] = useState(location?.state?.queryParams || initialQueryParams);

    useEffect(() => {
        const params = {
            ..._.omit(queryParams, [
                queryParams?.attributeId === 'none' && 'attributeId',
                queryParams?.foodPartnerId === 'none' && 'foodPartnerId',
                queryParams?.recommended === 'none' && 'recommended',
                queryParams?.defaultPicture === 'none' && 'defaultPicture'
            ])
        };

        dispatch(getProductsAction(params))
            .then(response => showErrorMessage(getProductsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, queryParams]);

    useEffect(() => {
        dispatch(getAllFoodPartnersAction())
            .then(response => showErrorMessage(getAllFoodPartnersNotificationData(response)));
        dispatch(getConstantsAction())
            .then(response => showErrorMessage(getConstantsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleSortChange = (newSort) => {
        setQueryParams(prevState => deepmerge(prevState, { sort: newSort, page: 0 }));
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader
                title="Produkty"
                actions={
                    <Button onClick={ () => navigate(ROUTE_PRODUCT_FORMS_NEW) }>
                        Dodaj produkt
                    </Button>
                }
            />

            { (getAllFoodPartners?.data && getConstants?.data) && (
                <ProductFilters queryParams={ queryParams } setQueryParams={ setQueryParams }/>
            ) }

            { getProducts?.loading && (
                <Box sx={ { width: '100%' } }>
                    <LinearProgress/>
                </Box>
            ) }

            { getProducts?.data?.content && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <ProductsList
                        queryParams={ queryParams }
                        onSortChange={ handleSortChange }
                        currentSort={ queryParams.sort }
                    />

                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getProducts?.data?.pageable?.pageNumber }
                            totalPages={ getProducts?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default ProductsPage;